import React, { useMemo, useCallback, useState } from 'react';
import { Play, Pause, ChevronDown, ChevronRight, Repeat } from 'lucide-react';
import { useSpineRuntimeStore } from '../../../store/spineStore';

interface SpineAnimationListProps {
  spineAssetId: string | null;
  instanceId: string | null;
  currentAnimation: string;
  isPlaying: boolean;
  defaultLoop?: boolean;
  onPlay: (animationName: string, loop?: boolean) => void;
  onStop: () => void;
}

export const SpineAnimationList: React.FC<SpineAnimationListProps> = ({
  spineAssetId,
  instanceId,
  currentAnimation,
  isPlaying,
  defaultLoop = true,
  onPlay,
  onStop,
}) => {
  const loadedAssets = useSpineRuntimeStore((s) => s.loadedAssets);
  const [isExpanded, setIsExpanded] = useState(true);
  const [loopEnabled, setLoopEnabled] = useState(defaultLoop);

  const animations = useMemo(() => {
    if (!spineAssetId || !loadedAssets[spineAssetId]) {
      return [];
    }
    return loadedAssets[spineAssetId].animations;
  }, [spineAssetId, loadedAssets]);

  const handlePlayClick = useCallback(
    (animName: string) => {
      if (currentAnimation === animName && isPlaying) {
        onStop();
      } else {
        onPlay(animName, loopEnabled);
      }
    },
    [currentAnimation, isPlaying, onPlay, onStop, loopEnabled]
  );

  const handleLoopToggle = useCallback(() => {
    setLoopEnabled((prev) => !prev);
  }, []);

  if (animations.length === 0) {
    return (
      <div className="text-sm text-zinc-500 py-2">
        No animations available
      </div>
    );
  }

  return (
    <div className="border border-zinc-700 rounded overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsExpanded(!isExpanded); }}
        className="w-full flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 cursor-pointer"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-zinc-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-zinc-400" />
        )}
        <span className="text-sm text-zinc-200 flex-1 text-left">
          Animations ({animations.length})
        </span>
        {/* Global loop toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleLoopToggle();
          }}
          className={`p-1 rounded ${
            loopEnabled
              ? 'bg-purple-600 text-white'
              : 'hover:bg-zinc-700 text-zinc-500'
          }`}
          title={loopEnabled ? 'Loop enabled' : 'Loop disabled'}
        >
          <Repeat className="w-3 h-3" />
        </button>
      </div>

      {isExpanded && (
        <div className="max-h-48 overflow-y-auto">
          {animations.map((anim) => {
            const isActive = currentAnimation === anim.name;
            const isAnimPlaying = isActive && isPlaying;

            return (
              <div
                key={anim.name}
                className={`flex items-center gap-2 px-3 py-1.5 border-t border-zinc-700 ${
                  isActive ? 'bg-purple-600/20' : 'hover:bg-zinc-800'
                }`}
              >
                <button
                  onClick={() => handlePlayClick(anim.name)}
                  className={`p-1 rounded ${
                    isAnimPlaying
                      ? 'bg-purple-600 text-white'
                      : 'hover:bg-zinc-700 text-zinc-400'
                  }`}
                >
                  {isAnimPlaying ? (
                    <Pause className="w-3 h-3" />
                  ) : (
                    <Play className="w-3 h-3" />
                  )}
                </button>

                <span className="flex-1 text-sm text-zinc-300 truncate">{anim.name}</span>

                <span className="text-xs text-zinc-500">
                  {anim.duration.toFixed(2)}s
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
